import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "@/lib/api";
import { Menu, X } from "lucide-react";

export const Nav = ({ overHero = false }) => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { scrollY } = useScroll();
  // Very subtle parallax: the bar drifts up a few px as the hero scrolls past.
  const floatY = useTransform(scrollY, [0, 320], [0, -6]);

  useEffect(() => {
    if (isAdmin) return;
    api.get("/public/nav").then((r) => setLinks(r.data || [])).catch(() => setLinks([]));
  }, [isAdmin]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  // "light" = transparent bar floating over a dark hero (white text).
  const light = overHero && !scrolled && !open;
  const publicLinks = links.map((l) => ({ to: `/${l.prefix}`, label: l.label, key: l.prefix }));

  const linkCls = light
    ? "text-white/85 hover:text-white transition-colors whitespace-nowrap"
    : "text-[#1A1B18] hover:text-[#96836B] transition-colors whitespace-nowrap";
  const subtleCls = light
    ? "text-[11px] uppercase tracking-[0.24em] text-white/80 hover:text-white transition-colors"
    : "text-[11px] uppercase tracking-[0.24em] text-[#737373] hover:text-[#1A1B18] transition-colors";

  return (
    <motion.header
      data-testid="main-nav"
      data-mode={light ? "transparent" : "solid"}
      style={{ y: floatY }}
      initial={{ opacity: 0, y: -18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow,padding] duration-500 ease-out ${
        light
          ? "bg-transparent border-b border-transparent"
          : "bg-[#FAF9F6]/80 backdrop-blur-xl border-b border-[#EAE8E3]/70 shadow-[0_8px_30px_-18px_rgba(26,27,24,0.35)]"
      }`}
    >
      <div className={`max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between transition-[padding] duration-500 ease-out ${scrolled ? "py-3.5" : "py-6"} ${light ? "[text-shadow:0_1px_12px_rgba(0,0,0,0.35)]" : ""}`}>
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className={`font-serif-display text-2xl tracking-tight ${light ? "text-white" : "text-[#1A1B18]"}`}>Deal&nbsp;Travel</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#96836B] group-hover:scale-125 transition-transform" />
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          {!isAdmin && publicLinks.map((l) => (
            <Link key={l.key} to={l.to} data-testid={`nav-${l.key}`} className={linkCls}>{l.label}</Link>
          ))}
          {!isAdmin && (
            <>
              <Link to="/carnets" data-testid="nav-carnets" className={linkCls}>Carnets</Link>
              <a href="/#temoignages" data-testid="nav-testimonials" className={linkCls}>Témoignages</a>
            </>
          )}
          <Link to={pathname === "/admin" ? "/" : "/admin"} data-testid="nav-admin-toggle" className={subtleCls}>
            {pathname === "/admin" ? "Site public" : "Espace admin"}
          </Link>
          {isAdmin && (
            <>
              <Link to="/admin/proposals" data-testid="nav-proposals" className={subtleCls}>Générateur de devis</Link>
              <Link to="/admin/content" data-testid="nav-content" className={subtleCls}>Centre de contenu</Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a href="/#devis" data-testid="nav-cta-devis"
            className={`hidden sm:inline-block text-[11px] uppercase tracking-[0.24em] px-5 py-2.5 transition-colors whitespace-nowrap border ${
              light
                ? "border-white/70 text-white hover:bg-white hover:text-[#1A1B18]"
                : "border-[#1A1B18] text-[#1A1B18] hover:bg-[#1A1B18] hover:text-[#FAF9F6]"
            }`}>
            Créer mon voyage sur-mesure
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            className={`md:hidden flex items-center justify-center w-10 h-10 -mr-2 ${light ? "text-white" : "text-[#1A1B18]"}`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#EAE8E3] bg-[#FAF9F6]/95 backdrop-blur-xl" data-testid="nav-mobile-panel">
          <nav className="px-6 py-6 flex flex-col divide-y divide-[#EAE8E3]">
            {!isAdmin && publicLinks.map((l) => (
              <Link key={l.key} to={l.to} data-testid={`nav-mobile-${l.key}`} className="py-4 text-lg font-serif-display hover:text-[#96836B] transition-colors">{l.label}</Link>
            ))}
            {!isAdmin && (
              <>
                <Link to="/carnets" data-testid="nav-mobile-carnets" className="py-4 text-lg font-serif-display hover:text-[#96836B] transition-colors">Carnets</Link>
                <a href="/#temoignages" data-testid="nav-mobile-testimonials" className="py-4 text-lg font-serif-display hover:text-[#96836B] transition-colors" onClick={() => setOpen(false)}>Témoignages</a>
              </>
            )}
            {isAdmin && (
              <>
                <Link to="/admin/proposals" className="py-4 text-lg font-serif-display" data-testid="nav-mobile-proposals">Générateur de devis</Link>
                <Link to="/admin/content" className="py-4 text-lg font-serif-display" data-testid="nav-mobile-content">Centre de contenu</Link>
              </>
            )}
            <Link to={pathname === "/admin" ? "/" : "/admin"} className="py-4 text-[11px] uppercase tracking-[0.24em] text-[#737373]" data-testid="nav-mobile-admin-toggle">
              {pathname === "/admin" ? "Site public" : "Espace admin"}
            </Link>
            <a href="/#devis" data-testid="nav-mobile-cta" className="mt-5 text-center text-[11px] uppercase tracking-[0.24em] border border-[#1A1B18] px-5 py-4 hover:bg-[#1A1B18] hover:text-[#FAF9F6] transition-colors" onClick={() => setOpen(false)}>
              Créer mon voyage sur-mesure
            </a>
          </nav>
        </div>
      )}
    </motion.header>
  );
};

export default Nav;
